import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Share2, Copy, Check, MessageSquare, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Production-facing share URL. Pointed at the live custom domain (mapped
 * via Emergent + Entri DNS).
 */
const PRIMARY_SHARE_URL = 'https://thirrsty.com';
const SHARE_TITLE = 'Thirrsty — Nightlife & QR Discounts';
const SHARE_TEXT =
  'Join me on Thirrsty — scan your QR at bars, clubs, lounges, coffee shops, vineyards & breweries for instant discounts. Free profile.';

/**
 * Reusable Share dialog. Pass a `trigger` element (button, icon, etc.).
 * When the user opens it they see: native Share button (if supported),
 * a QR code, and a copyable link with quick SMS/email/WhatsApp shortcuts.
 */
export function ShareAppDialog({ trigger, url = PRIMARY_SHARE_URL, title = SHARE_TITLE, text = SHARE_TEXT }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const supportsNativeShare = useMemo(
    () => typeof navigator !== 'undefined' && typeof navigator.share === 'function',
    []
  );

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy — long-press the link to copy manually');
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, text, url });
    } catch (e) {
      // user cancelled or share not available; silent
    }
  };

  const encodedText = encodeURIComponent(`${text} ${url}`);
  const smsHref = `sms:?&body=${encodedText}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}`;
  const waHref = `https://wa.me/?text=${encodedText}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Trigger is rendered by parent and clicks bubble up via onClick */}
      <div onClick={() => setOpen(true)} data-testid="share-app-trigger-wrapper">
        {trigger}
      </div>
      <DialogContent className="bg-[#0E0E14] border-white/15 text-white max-w-sm" data-testid="share-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display" style={{ fontFamily: 'Space Grotesk' }}>
            <Share2 className="h-5 w-5 text-[#F6B24A]" /> Share Thirrsty
          </DialogTitle>
          <DialogDescription className="text-white/65">
            Invite friends to your nightlife crew. They scan, they save.
          </DialogDescription>
        </DialogHeader>

        {/* QR Code */}
        <div className="flex justify-center py-2">
          <div className="rounded-2xl bg-white p-3 shadow-lg" data-testid="share-app-qr-wrapper">
            <QRCodeSVG
              value={url}
              size={180}
              level="M"
              fgColor="#07070A"
              bgColor="#FFFFFF"
              includeMargin={false}
            />
          </div>
        </div>
        <p className="-mt-1 text-center text-xs text-white/55">Point a phone camera at the code to open Thirrsty</p>

        {/* Link + copy */}
        <div className="mt-1 flex items-center gap-2">
          <Input
            value={url}
            readOnly
            className="bg-white/5 border-white/15 text-white text-sm font-mono truncate"
            onFocus={(e) => e.target.select()}
            data-testid="share-app-url-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleCopy}
            className="shrink-0 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/15"
            data-testid="share-app-copy-button"
            aria-label="Copy link"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>

        {/* Native share (mobile) */}
        {supportsNativeShare && (
          <Button
            onClick={handleNativeShare}
            className="mt-1 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#F6B24A]/90 font-semibold"
            data-testid="share-app-native-button"
          >
            <Share2 className="mr-2 h-4 w-4" /> Share via…
          </Button>
        )}

        {/* Quick channels */}
        <div className="grid grid-cols-3 gap-2">
          <a
            href={smsHref}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/85 hover:bg-white/10 transition-colors"
            data-testid="share-app-sms-link"
          >
            <MessageSquare className="h-5 w-5 text-[#2DE2E6]" />
            <span>Text</span>
          </a>
          <a
            href={mailHref}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/85 hover:bg-white/10 transition-colors"
            data-testid="share-app-email-link"
          >
            <Mail className="h-5 w-5 text-[#F6B24A]" />
            <span>Email</span>
          </a>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/85 hover:bg-white/10 transition-colors"
            data-testid="share-app-whatsapp-link"
          >
            <Send className="h-5 w-5 text-emerald-300" />
            <span>WhatsApp</span>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareAppDialog;
